/**
 * EventTextMeToggle.tsx — the "💬 Text me about this one" control. #1966, her
 * item 11.
 *
 * ⚠️ ONE COMPONENT FOR THREE SURFACES — the Add-event sheet, the ✏️ popover
 * and the Schedule Import rows — because the thing most likely to go wrong
 * here is not the write, it is the three copies disagreeing about what a free
 * account sees. A disabled toggle in one place and an enabled one in another
 * means a success toast over a row RLS refused.
 *
 * ⚠️ THE FREE-TIER RULE, which is Kasandra's and is unusual on purpose: the
 * toggle is VISIBLE AND DISABLED with one line of explanation. No upsell
 * modal. Hiding it would hide the feature; a modal in the middle of saving an
 * event is a dead end. So it reads as "this exists and isn't yours yet",
 * which is the truth.
 *
 * The offset picker is the SAME list as the calendar reminder next to it
 * (REMINDER_OPTIONS, minus "None" — "None" here is the toggle being off), so
 * the two controls cannot offer different notice periods for the same event.
 */

import { useId } from "react";
import { REMINDER_OPTIONS, type ReminderValue } from "@/lib/reminderOptions";
import {
  TEXT_ME_HELPER,
  TEXT_ME_NEEDS_PHONE_NOTE,
  TEXT_ME_PRO_NOTE,
  TEXT_ME_TOGGLE_LABEL,
  textMeSnappedNote,
} from "@/lib/eventTextMeCopy";

/** "None" is the toggle being off, so it is not an offset. */
export const TEXT_ME_OFFSET_OPTIONS = REMINDER_OPTIONS.filter((o) => o.value !== "none");

/** The default when she switches it on: an hour's notice. */
export const TEXT_ME_DEFAULT_OFFSET: ReminderValue = "60";

export interface EventTextMeToggleProps {
  enabled: boolean;
  offset: ReminderValue;
  onChange: (next: { enabled: boolean; offset: ReminderValue }) => void;
  /** `proBlocked` from the caller — `proLoaded && !isProTier`. NEVER `!isPro`:
   *  that is true while entitlements load and would flash a Pro note at a
   *  paying user (#616). */
  proBlocked: boolean;
  /** Set when the chosen offset had to be moved out of quiet hours, so she can
   *  see the real time before she saves. */
  snappedLocalTime?: string | null;
  /** A reason the reminder cannot be placed at all, already turned into a
   *  sentence by `textMeUnavailableNote`. */
  unavailableNote?: string | null;
  /** Compact variant for an import row, which has far less width. */
  compact?: boolean;
  /**
   * ⚠️ Kin has no way to text her — no verified phone, or STOP set.
   *
   * A SECOND disabling reason from `proBlocked`, deliberately, because the
   * sentence she needs is different: Pro is something to buy and this is
   * something to fix in Settings. Collapsing them would send a paying user to
   * the upgrade page over a missing phone number.
   */
  noPhone?: boolean;
  /**
   * The event repeats, so a per-event reminder has nothing single to attach
   * to. Disabled with its own line for the same reason as above.
   */
  repeatBlocked?: boolean;
  /** The one line to show for `repeatBlocked`. */
  repeatNote?: string;
}

const NOTE_STYLE: React.CSSProperties = {
  fontSize: 12,
  color: "#6B6B8F",
  marginTop: 4,
  lineHeight: 1.4,
};

const EventTextMeToggle = ({
  enabled,
  offset,
  onChange,
  proBlocked,
  snappedLocalTime,
  unavailableNote,
  compact = false,
  noPhone = false,
  repeatBlocked = false,
  repeatNote,
}: EventTextMeToggleProps) => {
  // useId, not a hand-rolled counter: two of these mount at once in the import
  // card and a duplicated htmlFor points every label at the first checkbox.
  const checkboxId = useId();
  const selectId = useId();

  /* ⚠️ ONE disabled flag, THREE sentences, and the order is the order of
   * precedence. Pro first because it is the one she cannot act on from here;
   * then the repeat, which is about this particular event; then the phone,
   * which is a Settings trip. Showing two notes at once would be two answers
   * to "why can't I use this". */
  const disabled = proBlocked || noPhone || repeatBlocked;
  const blockedNote = proBlocked
    ? TEXT_ME_PRO_NOTE
    : repeatBlocked
      ? (repeatNote ?? null)
      : noPhone
        ? TEXT_ME_NEEDS_PHONE_NOTE
        : null;

  return (
    <div data-kin-text-me-toggle style={{ marginTop: compact ? 8 : 14 }}>
      <label
        htmlFor={checkboxId}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: compact ? 13 : 14,
          color: disabled ? "#9A9AB8" : "#1C1A3E",
          fontWeight: 500,
        }}
      >
        <input
          id={checkboxId}
          type="checkbox"
          data-kin-text-me-checkbox
          checked={enabled && !disabled}
          disabled={disabled}
          onChange={(e) =>
            onChange({
              enabled: e.target.checked,
              // Switching it on with nothing chosen yet picks the default
              // rather than leaving the select at whatever it last showed.
              offset: e.target.checked && !offset ? TEXT_ME_DEFAULT_OFFSET : offset,
            })
          }
          style={{ width: 18, height: 18, accentColor: "#5050C8" }}
        />
        {TEXT_ME_TOGGLE_LABEL}
      </label>

      {/* ⚠️ The blocked note renders whether or not she has toggled anything,
          and it is the ONLY thing a blocked account is told. No modal, no
          /pro link mid-save. */}
      {blockedNote && <div style={NOTE_STYLE} data-kin-text-me-blocked>{blockedNote}</div>}

      {!disabled && enabled && (
        <div style={{ marginTop: 8 }}>
          <label
            htmlFor={selectId}
            style={{ display: "block", fontSize: 12, color: "#6B6B8F", marginBottom: 4 }}
          >
            How much notice?
          </label>
          <select
            id={selectId}
            data-kin-text-me-offset
            value={offset}
            onChange={(e) => onChange({ enabled: true, offset: e.target.value as ReminderValue })}
            style={{
              width: "100%",
              padding: compact ? "8px 10px" : "10px 12px",
              borderRadius: 10,
              border: "1.5px solid #C8C8F4",
              fontSize: 14,
              color: "#1C1A3E",
              boxSizing: "border-box",
              // ⚠️ No `background` here. #578 is about `type=date`/`type=time`
              // inputs, which WebKit paints with a FILLED native appearance
              // that reads as disabled; a <select> has no such problem, and the
              // shipped reminder select next to this one sets none either.
              WebkitAppearance: "none",
              appearance: "none",
            }}
          >
            {TEXT_ME_OFFSET_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {/* Order matters: an unavailable reason REPLACES the helper, because
              "Kin texts you before it starts" next to "there's no slot left"
              is two answers to one question. */}
          {unavailableNote ? (
            <div style={{ ...NOTE_STYLE, color: "#B3261E" }} data-kin-text-me-unavailable>
              {unavailableNote}
            </div>
          ) : snappedLocalTime ? (
            <div style={NOTE_STYLE} data-kin-text-me-snapped>
              {textMeSnappedNote(snappedLocalTime)}
            </div>
          ) : (
            <div style={NOTE_STYLE}>{TEXT_ME_HELPER}</div>
          )}
        </div>
      )}
    </div>
  );
};

export default EventTextMeToggle;
